/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2024 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

export const SYLLABUS_ACTIONS = {
  SET_FILE: 'SET_FILE',
  REMOVE_FILE: 'REMOVE_FILE',
  START_UPLOAD: 'START_UPLOAD',
  SET_PROGRESS: 'SET_PROGRESS',
  UPLOAD_SUCCESS: 'UPLOAD_SUCCESS',
  UPLOAD_ERROR: 'UPLOAD_ERROR',
  RESET_UPLOAD: 'RESET_UPLOAD',
  SET_TAB: 'SET_TAB',
  SET_ASSIGNMENTS: 'SET_ASSIGNMENTS',
  SET_DRAG_ACTIVE: 'SET_DRAG_ACTIVE',
  ADD_ASSIGNMENT: 'ADD_ASSIGNMENT',
  UPDATE_ASSIGNMENT: 'UPDATE_ASSIGNMENT',
  DELETE_ASSIGNMENT: 'DELETE_ASSIGNMENT',
  SET_INITIAL_SYLLABUS: 'SET_INITIAL_SYLLABUS',
  SET_DATE_ADJUST_PARAMS: 'SET_DATE_ADJUST_PARAMS',
  BULK_UPDATE_ASSIGNMENTS: 'BULK_UPDATE_ASSIGNMENTS',
  SET_LAST_ASSIGNMENT_DEFAULTS: 'SET_LAST_ASSIGNMENT_DEFAULTS'
};

export const DAYS_MAP = { Su: 0, M: 1, T: 2, W: 3, Th: 4, F: 5, S: 6 };

export const BULK_EDIT_MODE = 'bulk-edit';

// Tab IDs for syllabus upload
export const TAB_IDS = {
  UPLOAD: 'upload',
  ADD_MANUALLY: 'addmanually',
  DATE_ADJUSTER: 'dateadjuster'
};

// Default tab order configuration
export const DEFAULT_TAB_ORDER = [
  { index: 0, id: TAB_IDS.UPLOAD },
  { index: 1, id: TAB_IDS.ADD_MANUALLY }
];

export const ASSIGNMENT_TYPES = {
  ASSIGNMENT: 'assignment',
  EXAM: 'exam',
  HOMEWORK: 'homework'
};

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const BUTTON_TYPES = {
  ICON: 'icon',
  BUTTON: 'button'
};
export const COMPLETED = 'completed';

export const PROGRESS_STATUS = {
  COMPLETED: 'completed',
  IN_PROGRESS: 'inprogress',
  NOT_STARTED: 'notstarted'
};

export const PERFORMANCE_STATUS = {
  GREAT: 'great',
  GETTING_THERE: 'gettingthere',
  NEEDS_REVIEW: 'needsreview',
  COMPLETED_PAST_DUE: 'completedPastDue',
  NA: 'n/a',
  INPROGRESS: 'inprogress',
  COMPLETED: 'completed',
  NOTSTARTED: 'notstarted',
  OKAY: 'okay',
  NEEDS_WORK: 'needsWork'
};

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark'
};
export const ASSIGNMENT_FORM_ACTIONS = {
  SAVE: 'save',
  SAVE_AND_ADD: 'saveAndAdd',
  DELETE: 'delete',
  SAVE_AND_CLOSE: 'saveAndClose',
  CANCEL: 'cancel'
};

export const FLASHCARD_LABEL = 'View flash cards';
export const NEEDS_REVIEW_SECTION = 'Needs review';
export const SELECTION_TYPE = {
  TOPIC: 'topic',
  HEADER: 'header'
};

export const TIMEFIELD_CONSTANTS = {
  DEFAULT_HOUR: '12',
  DEFAULT_MINUTE: '00',
  DEFAULT_PERIOD: 'pm',
  DEFAULT_HOUR_DISPLAY: '01',
  DEFAULT_MINUTE_DISPLAY: '00',
  HOUR_MIN: 1,
  HOUR_MAX: 12,
  MINUTE_MIN: 0,
  MINUTE_MAX: 59,
  MAX_INPUT_LENGTH: 2,
  TIME: '',
  FIELD_TYPES: {
    HOUR: 'hour',
    MINUTE: 'minute',
    PERIOD: 'period'
  },
  PERIOD_VALUES: {
    AM: 'am',
    PM: 'pm'
  },
  KEY_CODES: {
    ARROW_UP: 'ArrowUp',
    ARROW_DOWN: 'ArrowDown',
    ARROW_LEFT: 'ArrowLeft',
    ARROW_RIGHT: 'ArrowRight',
    SPACE: ' ',
    ENTER: 'Enter'
  },
  DEFAULT_FORM_TIME: '23:59'
};
export const PERFORMANCE_STATUS_LABEL = {
  GREAT: 'Great',
  OKAY: 'Okay',
  NEEDS_REVIEW: 'Needs review'
};
export const PREFIX_ASSIGNMENT = 'assignment';

export const DATE_NORMALIZATION_ACTION = {
  YEAR_ADJUSTED: 'YEAR_ADJUSTED',
  MANUAL_ENTRY_REQUIRED: 'MANUAL_ENTRY_REQUIRED'
};

export const COVERAGE_DEDUPE_ACTION = {
  ADJUSTED: 'COVERAGE_ADJUSTED'
};
export const VALIDATEDATE_CONSTANTS = {
  NO_DATE: 'NO_DATE',
  DUE_DATE_REQUIRED: 'Due date is required',
  INVALID_DATE: 'INVALID_DATE',
  INVALID_DATE_FORMAT: 'Invalid date format',
  PAST_DUE_DATE: 'PAST_DUE_DATE',
  DUE_DATE_PAST: 'Due date and time is in the past',
  DUE_DATE_IN_THE_PAST: 'Due date is in the past',
  NO_CHAPTERS: 'NO_CHAPTERS',
  NO_PROPERLY_CHAPTERS: 'No properly mapped chapters',
  NO_QUESTIONS: 'NO_QUESTIONS',
  SELECTED_CHAPTERS_QUESTIONS: 'Selected chapters have no questions'
};

export const BULK_EDIT_MODAL_CONSTANTS = {
  TABLE_MAX_HEIGHT: 260,
  MODAL_MAX_HEIGHT: 980,
  MODAL_VERTICAL_PADDING: 100,
  RESERVED_NON_TABLE_HEIGHT: 260,
  MIN_TABLE_HEIGHT: 260
};

export const LOCAL_DELETE_TYPE = {
  SINGLE: 'single',
  BULK: 'bulk'
};

export const SYLLABUS_UI_CONSTANTS = {
  CHECKBOX_CONTAINER_MIN_HEIGHT: 140,
  CHECKBOX_CONTAINER_MAX_HEIGHT: 478,
  ADD_MODAL_CHECKBOX_CONTAINER_MAX_HEIGHT: 595
};

export const THREE_DOT_MENU_CONSTANTS = {
  COURSE_SETTINGS: 'course-settings',
  PEARSON_EBOOK_RESOURCES: 'pearson-ebook-resources',
  ETEXTBOOK_INFORMATION: 'eTextbook-information'
};

export const BOOK_DOWNLOAD_STATUS = {
  NOT_DOWNLOADED: 'NOT_DOWNLOADED',
  EXTRACTING: 'EXTRACTING',
  COMPLETED: 'COMPLETED',
  OPENED: 'OPENED'
};

export const POSITIONS = {
  TOP: 'top',
  BOTTOM: 'bottom'
};

export const FOOTER_CTA_LINKS = {
  ASK_AI: 'ask-ai',
  REVIEW_READING: 'review-reading'
};
export const TOPIC_STYLES = 'topicStyles';

export const REPORT_QUESTION_REASONS = [
  { id: 'notRelevant', label: 'Not relevant', value: 'Not relevant' },
  { id: 'notCorrect', label: 'Not correct', value: 'Not correct' },
  { id: 'unsafe', label: 'Unsafe/harmful', value: 'Unsafe/harmful' },
  { id: 'somethingElse', label: 'Something else', value: 'Something else' }
];

export const SHOW_PATTERN_IN_AI_USE = false;
export const CARD_BOUNDARY = 0.99;
export const PREV = 'Previous';
export const NEXT = 'Next';
export const SPACEKEY = 'Space';
export const ESCAPEKEY = 'Escape';
export const ENTERKEY = 'Enter';

export const ROSTER_FILTER_OPTIONS = [
  {
    id: 'allMembers',
    label: 'All members',
    value: 'all'
  },
  {
    id: 'activeStudents',
    label: 'Active students',
    value: 'Active',
    filters: {
      status: 'Active',
      role: 'Student'
    }
  },
  {
    id: 'inactiveStudents',
    label: 'Inactive students',
    value: 'Inactive',
    filters: {
      status: 'Inactive',
      role: 'Student'
    }
  }
];


export const ROSTER_ROLE_OPTIONS = [
  { label: 'Student', value: 'Student' },
  { label: 'TA', value: 'TA' },
  { label: 'Instructor', value: 'Instructor' }
];

export const ROSTER_STATUS_OPTIONS = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' }
];

